import isNil from 'ramda/src/isNil'
import pick from 'ramda/src/pick'
import mixpanel from 'root/src/client/logic/analytics/mixpanel'
import { mixpanelEvents } from 'root/src/shared/constants/mixpanel'
import updateEventDare from 'root/src/client/logic/dropEvent/actions/updateEventDare'
import { CREATE_PROJECT_DROP, GET_USER_DATA } from 'root/src/shared/descriptions/endpoints/endpointIds'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import wait from 'root/src/shared/util/wait'
import { DROP_EVENT_PAGE_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds'
import {
	GET_VERIFIED_MODAL_MODULE_ID,
	SUCCESS_MODAL_MODULE_ID,
} from 'root/src/shared/descriptions/modules/moduleIds'
import displayModal from 'root/src/client/logic/modal/thunks/displayModal'
import identity from 'ramda/src/identity'
import getDomainRoute from 'root/src/client/util/getDomainRoute'
import { events } from 'root/src/shared/constants/analytics'
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem'
import userIdSelector from 'root/src/client/logic/auth/selectors/userIdSelector'
import userGroupsSelector from 'root/src/client/logic/auth/selectors/userGroupsSelector'
import { AuthGroup } from 'root/src/server/util/awsClients/authApiClient.types'
import { authModuleIds } from 'root/src/client/web/auth/descriptions/modules/authModuleIds'
import { displayHowToEarnPointsModal } from 'root/src/client/logic/dropEvent/thunks/displayHowToEarnPointsModal'
import { displayNoMoreRewardModal } from 'root/src/client/logic/dropEvent/thunks/displayNoMoreRewardModal'
import { displayUpdateContactInformationModal } from 'root/src/client/logic/project/helpers/displayUpdateContactInformationModal'
import accountDataSelector from 'root/src/client/logic/userData/selectors/accountDataSelector'

// eslint-disable-next-line prefer-template
export const generateMaxDaresText = (followerCount, averageCcv, maxDares) => 'Based on your '
	+ (!isNil(followerCount) ? `follower count of ${followerCount}` : '')
	+ (!isNil(followerCount) && !isNil(averageCcv) ? ' and ' : '')
	+ (!isNil(averageCcv) ? `30 day concurrent view avg of ${averageCcv}` : '')
	+ `, you can complete ${maxDares} dare${maxDares === 1 ? '' : 's'}.`

export default (formData, showModalFn = identity) => async (dispatch, getState) => {
	const state = getState()

	const userGroups = userGroupsSelector(state)
	const isCreator = userGroups.includes(AuthGroup.Creator)

	if (!isCreator) {
		dispatch(displayModal(authModuleIds.TRANSFORM_TO_CREATOR))
		throw { error: 'To be able to accept Dare project, you need to be a Creator' }
	}

	const accountData = accountDataSelector(state)
	if (accountData.isConfirmEmailPromptEnabled) {
		const isConfirmed = await displayUpdateContactInformationModal()
		if (!isConfirmed) {
			return {}
		}
		await dispatch(apiRequest(GET_USER_DATA))
	}

	const { dropEvent } = formData
	const { id: campaignId, mnemonicId } = dropEvent
	const requestPayload = {
		...pick(['assignees', 'chosenInitialProductIndex', 'userHasGame', 'withoutReward'], formData),
		dropDareId: formData.dropDareId,
		campaignId,
	}
	const { error, body } = await dispatch(apiRequest(CREATE_PROJECT_DROP, requestPayload))

	if (error) {
		if (error.code === 'CampaignTermsNotAccepted') {
			dispatch(displayHowToEarnPointsModal())
			throw { error: 'You need to accept the campaign terms' }
		}

		if (error.code === 'FollowerCountTooLow') {
			throw { error: 'Your follower count is too low' }
		}

		if (error.code === 'NotEnoughKeys') {
			dispatch(displayNoMoreRewardModal({ formData }))
			throw { error: 'Not enough keys' }
		}

		if (error.code === 'InsufficientBattlePassTier') {
			throw { error: error.message }
		}

		if (error.code === 'NotEnoughInitialProducts') {
			throw { error: 'Out of keys, we\'ll email you when we get more' }
		}

		if (error.code === 'DeliveryRatioTooLow') {
			const { campaignsData } = error.props
			const campaignLinks = campaignsData.map(
				({ campaignName, campaignMnemonicId }) => ({
					linkText: campaignName,
					linkUrl: getDomainRoute(DROP_EVENT_PAGE_ROUTE_ID).replace(
						':recordId',
						campaignMnemonicId,
					),
				}),
			)
			dispatch(displayModal(SUCCESS_MODAL_MODULE_ID, {
				header: 'Delivery ratio is too low',
				errorText: 'You are not able to join this drop',
				...(campaignLinks.length > 0 ? {
					infoText: error.message,
					linkObjects: campaignLinks,
				} : { infoText: '' }),
			}))
			throw { error: 'Delivery ratio is too low' }
		}

		if (error.code === 'RequirementsNotSatisfied') {
			const { assigneesNotSatisfying } = error.props
			const [{ followerCount, averageCcv, maxDares, platform }] = assigneesNotSatisfying
			throw { error: generateMaxDaresText(followerCount, averageCcv, maxDares, platform) }
		}

		if (error.code === 'ProjectDuplicate' || error.code === 'SysError') {
			throw { error: error.message }
		}

		if (error.code === 'NotFound') {
			// dispatch(displayModal(NO_CAMPAIGN_MODAL_MODULE_ID))
			throw { error: 'Campaign is not active' }
		}

		if (error.code === 'VerificationPending') {
			dispatch(displayModal(GET_VERIFIED_MODAL_MODULE_ID, { isCloseable: false }))
			throw { error: error.message }
		}

		if (error.code === 'VerificationNotVerified') {
			dispatch(displayModal(SUCCESS_MODAL_MODULE_ID, {
				isCloseable: false,
				errorText: error.message,
				header: 'Your channel is not verified',
			}))
			throw { error: error.message }
		}

		if (error.code === 'InternalServerError') {
			throw { error: 'Internal Server Error' }
		}

		console.error(error)

		throw { error: 'Something went wrong' }
	}
	mixpanel.track(mixpanelEvents.projectCreated)
	mixpanel.track(mixpanelEvents.dropClaim)
	const userId = userIdSelector(state)
	analyticsSendItem(events.acceptedDropDare, `${dropEvent.campaignName} - ${userId}`)
	// used to show INITIAL_PRODUCT_DISPLAY_MODAL after succesful project creation
	showModalFn()

	dispatch(updateEventDare({
		dropDareId: formData.dropDareId,
		mnemonicId,
		status: body?.status,
		expirationDate: body?.expirationDate,
		landingShortUrl: body?.landingShortUrl,
	}))
	if (formData.onSuccess) {
		await formData.onSuccess(body)
	}
	// this wait is here because projects aren't instantly created/pledged with elasticSearch
	await wait(1000)
	return { error,	body }
}
